import _ from 'lodash';
import React, { useEffect, useState, useRef } from 'react';
import { Accordion } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { LoadingLogo } from '../../components/Modal';
import './UserView.css';
import AccountDisplay from './components/AccountDisplay';
import EditableParam from './components/EditableParam';
import FestivalProgressDisplay from './components/FestivalProgressDisplay';
import InfluencerDisplay from './components/InfluencerDisplay';
import ItemsAdd from './components/ItemsAdd';
import ItemsEditor from './components/ItemsEditor';
import LoyaltyDisplay from './components/LoyaltyDisplay';
import LoyaltyCampaignDisplay from './components/LoyaltyCampaignDisplay';
import MonthlyPassDisplay from './components/MonthlyPassDisplay';
import PiggyEditor from './components/PiggyEditor';
import MusicFragmentAdd from './components/MusicFragmentAdd';
import MusicFragmentEditor from './components/MusicFragmentEditor';
import VanityPiecesEditor from './components/VanityPiecesEditor';
import UserPlaylistsDisplay from './components/UserPlaylistsDisplay';
import SecretContentPackIdsDisplay from './components/SecretContentPackIdsDisplay';
import TransactionDisplay from './components/TransactionDisplay';
import RedeemCodeHistoryDisplay from './components/RedeemCodeHistoryDisplay';
import LiveShowDisplay from './components/LiveShowDisplay';
import Sp2LinkDisplay from './components/Sp2LinkDisplay';
import SpendTierDisplay from './components/SpendTierDisplay';
import MaestroPackDisplay from './components/MaestroPackDisplay';
import UserReportDisplay from './components/UserReportDisplay';
import { paramIcon } from './paramIcons';
import { getUnlimitedBoostDetails } from './unlimitedBoostDetails';
import { EDITABLE_PARAMS, ParamTypeValue, PARAM_TYPES, useUserView } from './useUserView';
import NotificationToolDisplay from './components/NotificationToolDisplay';
import AchievementMissionsDisplay from './components/AchievementMissionsDisplay';
import RetentionToolDisplay from './components/RetentionToolDisplay';
import { HurdleQuizDisplay } from './components/HurdleQuizDisplay';
import { UserSubscriptionLogsDisplay } from './components/UserSubscriptionLogDisplay';
import { CacheDisplay } from './components/CacheDisplay';
import YearInReviewDisplay from './components/YearInReviewDisplay';
import MetaFragmentsEditor from './components/MetaFragmentsEditor';
import UserTeamDisplay from './components/UserTeamDisplay';
import { UserEmojiPackDisplay } from './components/UserEmojiPackDisplay';
import PreviouslySearchedUsers from './components/PreviouslySearchedUsers';
import ScrollToTopButton from './components/ScrollToTopButton';
import UserExportDisplay from './components/UserExportDisplay';
import CalendarsEditor from './components/CalendarsEditor';
import { UserMarathonEventDisplay } from './components/UserMarathonEventDisplay';
import { UserPraCooldownDisplay } from './components/UserPraCooldownDisplay';
import { UserGachaShopDisplay } from './components/UserGachaShopDisplay';
import { UserTournamentDisplay } from './components/UserTournamentDisplay';
import UserGiftStatusDisplay from './components/UserGiftStatusDisplay';
import UserBadgesDisplay, { UserBadgesDisplayHandle } from './components/UserBadgesDisplay';
import FriendListDisplay from './components/FriendListDisplay';
import UserAttributeSection, { matchesSearch } from './components/UserAttributeSection';

const baseURL = '/sp3/devtools/userview';

// One-value attributes (balances, levels, energy...), shown together in the "attributes" section
const SCALAR_PARAM_TYPES: ParamTypeValue[] = [
  PARAM_TYPES.INT,
  PARAM_TYPES.INTORNULL,
  PARAM_TYPES.FLOAT,
  PARAM_TYPES.STRING,
  PARAM_TYPES.ENERGY,
];

// What react-router hands us when the URL is the route pattern itself instead of a real id
const ROUTE_USER_ID_PLACEHOLDER = ':userid';

// The search header sits outside the scroll flow, so this element — not the layout's
// container — is what scrolls. Keep every scroll consumer pointed at it.
const SCROLL_CONTAINER_SELECTOR = '.userview-page .uv-body';

const UserView = () => {
  const {
    handleInputChange,
    handleChangeUser,
    handleSearch,
    handleSaveUser,
    handleBanUser,
    handleUnbanUser,
    handleResetUserSubscriptions,
    handleUpdateSubscriptionExpires,
    handleForceABTest,
    handleSearchUrl,
    handleChangeMonthlyPass,
    handleChangePiggyBank,
    handleAddItem,
    handleAddAllItems,
    handleDeleteItem,
    handleChangeItemLevel,
    updateLoading,
    newUser,
    contentCategories,
    energyCaps,
    stickers,
    profileFrames,
    appSkins,
    data,
    input,
    handleChangeEarnedContentPackIds,
    handleChangeRecentPlaylist,
    handleSaveRecentPlaylist,
    handleChangeSearchedPlaylist,
    handleSaveSearchedPlaylist,
    handleSetSearchedPlaylistAsCurrentBasic,
    handleSearchPlaylist,
    handleAddSavedPlaylist,
    searchedPlaylist,
    playlistUpdateMessage,
    handleChangeFragment,
    handleChangeVanityPiece,
    updateStatus,
    hasSearched,
    clearSearch,
    getUserLoading,
    lastSearchTerm,
    searchQueryError,
    abTestConfigs,
    appInfoLoading,
    lastSession,
    handleKeyPressed,
    resetSubscriptionsLoading,
    handleChangeMetaFragments,
    handleChangeShards,
  } = useUserView();
  const { userid } = useParams<{ userid: string }>();
  const history = useHistory();

  const [expanded, setExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const handleHistoryClick = () => {
    setShowHistory((prev) => !prev);
  };
  const canShowHistoryButton = !!(data && newUser && abTestConfigs);
  const handleHistorySearchUrl = async (userId: string) => {
    await handleSearchUrl(userId);
    setShowHistory(false);
  };
  const userAttributesRef = useRef<HTMLDivElement | null>(null);
  // Set when the screen is being cleared on purpose, so the url sync skips that one commit
  const skipUrlSyncRef = useRef(false);
  const badgesRef = useRef<UserBadgesDisplayHandle | null>(null);

  const handleSaveAll = async () => {
    try {
      if (badgesRef.current?.hasPendingChanges()) {
        await badgesRef.current.flush();
      }
    } catch {
      // Badge save errors are surfaced inside UserBadgesDisplay; continue with user save.
    }
    handleSaveUser();
  };

  useEffect(() => {
    if (expanded && userAttributesRef.current) {
      const scrollContainer = document.querySelector(SCROLL_CONTAINER_SELECTOR) as HTMLElement | null;

      const VH_OFFSET = 32;
      const offset = (window.innerHeight * VH_OFFSET) / 100;
      const timer = setTimeout(() => {
        if (!scrollContainer || !userAttributesRef.current) {
          return;
        }

        const targetTop = userAttributesRef.current.offsetTop;
        const scrollToPosition = targetTop - offset;
        scrollContainer.scrollTo({
          top: scrollToPosition,
          behavior: 'smooth',
        });
      }, 100);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, [expanded]);
  useEffect(() => {
    if (userid && userid !== ROUTE_USER_ID_PLACEHOLDER) {
      // The url is also written from a successful search, so without this the search that
      // just ran would immediately run a second time — and SEARCH_USER is a large query.
      if (data?.id !== userid) {
        handleSearchUrl(userid);
      }
      return;
    }
    // Reached the screen without a user: the sidebar link, or an old link still carrying
    // the route pattern. The component stays mounted across that navigation, so without
    // this the previous user keeps being displayed — and its child queries keep firing.
    // clearSearch only lands on the next render, so the url sync below would still see the
    // old user this commit and put its id straight back. Flag the intent synchronously.
    skipUrlSyncRef.current = true;
    clearSearch();
    if (userid === ROUTE_USER_ID_PLACEHOLDER) {
      history.push(baseURL);
    }
  }, [history, userid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    // Only when the displayed user actually changes. `newUser` is the edit form, replaced
    // on every keystroke, so navigating on each of its changes would push a history entry
    // and re-render the whole screen per character. `replace` also keeps the back button
    // usable: one search should not bury the previous page under duplicate entries.
    if (data && newUser && userid !== data.id) {
      history.replace(`${baseURL}/${data.id}`);
    }
  }, [data, history, newUser, userid]);
  useEffect(() => {
    const scrollEl = document.querySelector(SCROLL_CONTAINER_SELECTOR);
    const target: HTMLElement | Window = (scrollEl as HTMLElement) || window;

    const handleScroll = () => {
      if (showHistory) {
        setShowHistory(false);
      }
    };

    if (showHistory) {
      target.addEventListener('scroll', handleScroll);
    }

    return () => {
      target.removeEventListener('scroll', handleScroll);
    };
  }, [showHistory]);

  const categories = newUser ? _.differenceBy(contentCategories, newUser.fragmentBalance, 'id') : [];

  return (
    <div className="userview-page">
      <div className="uv-search-sticky">
        <div className="userview-search-card uv-search-flat">
          <Form className="m-0">
            <div className="uv-search-row">
              <InputGroup className="uv-search-input-group">
                <InputGroup.Text className="uv-search-icon">
                  <i className="fas fa-search" />
                </InputGroup.Text>
                <Form.Control
                  value={input}
                  placeholder="Enter ID, name, email or phone number"
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPressed}
                />
              </InputGroup>

              <Button onClick={handleSearch}>Search</Button>

              {canShowHistoryButton && (
                <div className="position-relative">
                  <Button variant="primary" onClick={handleHistoryClick}>
                    History
                  </Button>

                  {showHistory && (
                    <div className="uv-history-popover">
                      <PreviouslySearchedUsers
                        handleInputChange={handleInputChange}
                        handleSearchUrl={handleHistorySearchUrl}
                        reRender={data}
                        loadExpanded={true}
                        onCollapse={() => setShowHistory(false)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </Form>
        </div>
      </div>
      <div className="uv-body">
        {/* Saving keeps the form on screen, so this one stays inline */}
        <LoadingLogo show={updateLoading} />
        {getUserLoading ? (
          <div className="uv-loading">
            <div className="uv-loading-snail" aria-hidden="true">
              <span className="uv-loading-snail-crawl">
                <span className="uv-loading-snail-bob">🐌</span>
              </span>
            </div>
            <span className="mt-3">Loading account…</span>
          </div>
        ) : data && newUser && abTestConfigs ? (
          <>
            <AccountDisplay
              account={data}
              abTestConfigsData={abTestConfigs}
              lastSession={lastSession}
              onForceTest={(testId: string, variation: string) => handleForceABTest(userid, testId, variation)}
              forceTestLoading={false}
              onBanUser={() => handleBanUser(userid)}
              onUnbanUser={() => handleUnbanUser(userid)}
              onResetUserSubscriptions={() => handleResetUserSubscriptions(userid)}
              onUpdateSubscriptionExpires={(expires: string) => handleUpdateSubscriptionExpires(userid, expires)}
              resetSubscriptionsLoading={resetSubscriptionsLoading}
              showHistory={showHistory}
            >
              <Card className="mb-1" ref={userAttributesRef}>
                <Card.Body>
                  <Accordion>
                    <Accordion.Toggle
                      as={Card.Title}
                      variant="link"
                      eventKey="0"
                      onClick={() => setExpanded(!expanded)}
                    >
                      <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                        User Attributes
                        <Button variant="link">
                          {!expanded && <i className="fas fa-chevron-down" />}
                          {expanded && <i className="fas fa-chevron-up" />}
                        </Button>
                      </div>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey="0">
                      <>
                        <Form>
                          <Row className="uv-attributes">
                            <Col md="12">
                              <UserAttributeSection title="attributes" searchPlaceholder="Search attribute">
                                {(search) => {
                                  const shownParams = EDITABLE_PARAMS.filter(
                                    ({ key, type }) =>
                                      SCALAR_PARAM_TYPES.includes(type as ParamTypeValue) && matchesSearch(key, search)
                                  );
                                  return shownParams.length > 0 ? (
                                    <Row>
                                      {shownParams.map(({ key, type }) =>
                                        (type as ParamTypeValue) === PARAM_TYPES.ENERGY ? (
                                          !appInfoLoading && (
                                            <Col md="6">
                                              <div className="uv-param-row">
                                                <EditableParam
                                                  key={key}
                                                  title={key}
                                                  icon={paramIcon(key).icon}
                                                  iconClassName={`uv-icon-${paramIcon(key).tone}`}
                                                  details={`max: ${
                                                    // @ts-ignore
                                                    energyCaps[data.subscriptionTier][
                                                      key.includes('XP')
                                                        ? 'xp'
                                                        : key.includes('Coin')
                                                        ? 'coin'
                                                        : key.includes('Tournament')
                                                        ? 'tournamentTicket'
                                                        : 'liveEventTicket'
                                                    ]
                                                  }`}
                                                  // @ts-ignore
                                                  value={newUser[key]}
                                                  onChange={handleChangeUser(key)}
                                                  // @ts-ignore
                                                  placeholder={data[key]}
                                                />
                                              </div>
                                            </Col>
                                          )
                                        ) : (
                                          <Col md="6">
                                            <div className="uv-param-row">
                                              <EditableParam
                                                key={key}
                                                title={key}
                                                icon={paramIcon(key).icon}
                                                iconClassName={`uv-icon-${paramIcon(key).tone}`}
                                                // @ts-ignore
                                                value={newUser[key]}
                                                onChange={handleChangeUser(key)}
                                                // @ts-ignore
                                                placeholder={data[key]}
                                                details={getUnlimitedBoostDetails(data?.unlimitedBoosts, key)}
                                              />
                                            </div>
                                          </Col>
                                        )
                                      )}
                                    </Row>
                                  ) : (
                                    <i>No match</i>
                                  );
                                }}
                              </UserAttributeSection>
                            </Col>
                            {EDITABLE_PARAMS.map(({ key, type }) => {
                              switch (type as ParamTypeValue) {
                                case PARAM_TYPES.INT:
                                case PARAM_TYPES.INTORNULL:
                                case PARAM_TYPES.FLOAT:
                                case PARAM_TYPES.STRING:
                                case PARAM_TYPES.ENERGY:
                                  // In the attributes section above
                                  return null;
                                case PARAM_TYPES.PLAYLIST:
                                  // basicTierContentPack rendered separately via
                                  // <UserPlaylistsDisplay> alongside savedContentPacks.
                                  return null;
                                case PARAM_TYPES.MONTHLY_PASS:
                                  // Rendered inside <MonthlyPassRewardsDisplay>, next to the rest
                                  // of this user's monthly pass data.
                                  return null;
                                case PARAM_TYPES.PIGGY_BANK:
                                  return (
                                    <Col md="12" key={key}>
                                      <UserAttributeSection title={key}>
                                        {(search) => (
                                          <PiggyEditor
                                            // @ts-ignore
                                            bank={newUser[key]}
                                            // @ts-ignore
                                            title={key}
                                            // @ts-ignore
                                            onChange={handleChangePiggyBank}
                                            search={search}
                                          />
                                        )}
                                      </UserAttributeSection>
                                    </Col>
                                  );

                                case PARAM_TYPES.PLAYLIST_MULTI:
                                  // Rendered separately via <UserPlaylistsDisplay> outside the
                                  // User Attributes accordion. Skip here to avoid duplication.
                                  return null;
                                case PARAM_TYPES.META_FRAGMENTS:
                                case PARAM_TYPES.SHARDS:
                                  return (
                                    <Col md="12" key={key}>
                                      <UserAttributeSection title={key}>
                                        {(search) => (
                                          <MetaFragmentsEditor
                                            // @ts-ignore
                                            meta={newUser[key]}
                                            // @ts-ignore
                                            title={key}
                                            // @ts-ignore
                                            onChange={
                                              type === PARAM_TYPES.SHARDS
                                                ? handleChangeShards
                                                : handleChangeMetaFragments
                                            }
                                            search={search}
                                          />
                                        )}
                                      </UserAttributeSection>
                                    </Col>
                                  );
                                case PARAM_TYPES.VANITY_PIECE:
                                  return (
                                    <Col md="12" key={key}>
                                      <VanityPiecesEditor
                                        title={key}
                                        // @ts-ignore
                                        balance={newUser[key] ?? []}
                                        onChange={handleChangeVanityPiece}
                                      />
                                    </Col>
                                  );
                                case PARAM_TYPES.FRAGMENT:
                                  return (
                                    <Col md="12" key={key}>
                                      <UserAttributeSection
                                        title={key}
                                        searchPlaceholder="Search category"
                                        actions={
                                          <MusicFragmentAdd categories={categories} onChange={handleChangeFragment} />
                                        }
                                      >
                                        {(search) => {
                                          const fragments: Array<{ id: string; category: { id: string } }> =
                                            _.get(newUser, key) ?? [];
                                          const shownFragments = fragments.filter((fragment) =>
                                            matchesSearch(
                                              contentCategories.find((category) => category.id === fragment.category.id)
                                                ?.name,
                                              search
                                            )
                                          );
                                          return shownFragments.length > 0 ? (
                                            <Row>
                                              {shownFragments.map((fragment) => (
                                                <Col key={fragment.id} md="auto" className="mb-4">
                                                  {/* @ts-ignore */}
                                                  <MusicFragmentEditor
                                                    // @ts-ignore
                                                    fragment={fragment}
                                                    onChange={handleChangeFragment}
                                                    contentCategories={contentCategories}
                                                  />
                                                </Col>
                                              ))}
                                            </Row>
                                          ) : (
                                            <p>
                                              <i>{fragments.length > 0 ? 'No match' : 'No fragments'}</i>
                                            </p>
                                          );
                                        }}
                                      </UserAttributeSection>
                                    </Col>
                                  );
                                case PARAM_TYPES.STICKERS:
                                case PARAM_TYPES.PROFILE_FRAMES:
                                case PARAM_TYPES.APP_SKINS: {
                                  const catalog =
                                    type === PARAM_TYPES.STICKERS
                                      ? stickers
                                      : type === PARAM_TYPES.PROFILE_FRAMES
                                      ? profileFrames
                                      : appSkins;
                                  return (
                                    <React.Fragment key={key}>
                                      <Col md="12">
                                        <UserAttributeSection
                                          title={key}
                                          actions={
                                            <ItemsAdd
                                              items={catalog}
                                              type={key}
                                              onAdd={handleAddItem}
                                              onAddAll={handleAddAllItems}
                                            />
                                          }
                                        >
                                          {(search) => {
                                            // @ts-ignore
                                            const items: Array<{ name: string }> = newUser[key];
                                            return (
                                              <ItemsEditor
                                                // @ts-ignore
                                                items={items.filter((item) => matchesSearch(item.name, search))}
                                                type={key}
                                                onDelete={handleDeleteItem}
                                                onChangeItemLevel={handleChangeItemLevel}
                                                emptyText={items.length > 0 ? 'No match' : 'No items'}
                                              />
                                            );
                                          }}
                                        </UserAttributeSection>
                                      </Col>
                                      {type === PARAM_TYPES.PROFILE_FRAMES && (
                                        <Col md="12">
                                          <UserBadgesDisplay ref={badgesRef} userId={userid} />
                                        </Col>
                                      )}
                                    </React.Fragment>
                                  );
                                }
                                case PARAM_TYPES.CALENDARS:
                                  return (
                                    <Col md="12" key={key}>
                                      <UserAttributeSection title={key}>
                                        {(search) => {
                                          // @ts-ignore
                                          const calendars: Array<{ id: string; name: string }> = newUser[key];
                                          return (
                                            <CalendarsEditor
                                              items={calendars.filter(
                                                (calendar) =>
                                                  matchesSearch(calendar.name, search) ||
                                                  matchesSearch(calendar.id, search)
                                              )}
                                              userId={userid}
                                              emptyText={calendars.length > 0 ? 'No match' : 'No calendars'}
                                            />
                                          );
                                        }}
                                      </UserAttributeSection>
                                    </Col>
                                  );
                                default:
                                  return <></>;
                              }
                            })}
                          </Row>
                        </Form>
                        <div className="userview-savebar">
                          <div className="d-flex justify-content-end align-items-center">
                            {updateStatus && (
                              <Alert
                                variant={updateStatus === 'failed' ? 'danger' : 'success'}
                                className="userview-savebar-alert"
                              >
                                {updateStatus === 'failed' ? 'Update failed' : 'Update Succeed'}
                              </Alert>
                            )}

                            {updateLoading ? (
                              <Spinner animation="border" />
                            ) : (
                              <Button className="mx-2" onClick={handleSaveAll}>
                                Save User
                              </Button>
                            )}
                          </div>
                        </div>
                      </>
                    </Accordion.Collapse>
                  </Accordion>
                </Card.Body>
              </Card>

              <UserPlaylistsDisplay
                userId={userid}
                recentPlaylists={newUser.recentContentPacks ?? []}
                onChangeRecent={handleChangeRecentPlaylist}
                onSaveRecent={handleSaveRecentPlaylist}
                searchedPlaylist={searchedPlaylist}
                onChangeSearched={handleChangeSearchedPlaylist}
                onSaveSearched={handleSaveSearchedPlaylist}
                onSetCurrentSearched={handleSetSearchedPlaylistAsCurrentBasic}
                onSearch={handleSearchPlaylist}
                onAdd={handleAddSavedPlaylist}
                updateMessage={playlistUpdateMessage}
              />
              <FriendListDisplay userId={userid} userName={data.displayName} />
            </AccountDisplay>

            <LoyaltyDisplay userId={userid} loyalty={data.loyalty} />
            <UserGiftStatusDisplay userId={userid} />
            <LoyaltyCampaignDisplay userId={userid} />
            <HurdleQuizDisplay userId={userid} />
            <UserSubscriptionLogsDisplay userId={userid} />
            <AchievementMissionsDisplay
              userId={userid}
              // @ts-ignore Type mismatch between codegen (completedDate optional) and local Mission type.
              missions={data.achievementMissions?.missions ?? []}
              missionSetUserId={data.achievementMissions?.id}
            />
            {/* @ts-ignore */}
            <MonthlyPassDisplay
              userId={userid}
              missions={data.currentUserMonthlyPass?.missions}
              currentMonthlyPass={newUser.userLevel >= 20 ? newUser.currentUserMonthlyPass : undefined}
              onChangeMonthlyPass={handleChangeMonthlyPass}
            />
            <SecretContentPackIdsDisplay
              onChange={handleChangeEarnedContentPackIds}
              earnedContentPacks={data.ownedContentPacks.list}
              // @ts-ignore - field lands in generated types on next codegen
              totalCount={data.earnedContentPackIdsRaw?.length}
            />
            <FestivalProgressDisplay
              userId={userid}
              specialEventId={data.currentSpecialEvent?.id}
              scoreDetails={data.currentSpecialEvent?.meScoreDetails}
            />
            {data.reports.length > 0 && <UserReportDisplay reports={data.reports} />}
            <TransactionDisplay userId={userid} />
            <RedeemCodeHistoryDisplay userId={userid} />
            <LiveShowDisplay userId={userid} />
            <YearInReviewDisplay userId={userid} />
            <Sp2LinkDisplay userId={userid} />
            <SpendTierDisplay userId={userid} />
            <MaestroPackDisplay userId={userid} />
            <CacheDisplay userId={userid} />
            <InfluencerDisplay
              userId={userid}
              isInfluencer={data.isInfluencer ?? false}
              username={data.displayName ?? ''}
              influencerPicture={data.picture ?? undefined}
            />
            <NotificationToolDisplay userId={userid} />
            <RetentionToolDisplay userId={userid} />
            <UserTeamDisplay userId={userid} teamId={newUser.team.id} />
            <UserEmojiPackDisplay userId={userid} />
            <UserMarathonEventDisplay userId={userid} />
            <UserPraCooldownDisplay userId={userid} />
            <UserGachaShopDisplay userId={userid} />
            <UserTournamentDisplay userId={userid} />
            <UserExportDisplay userId={userid} />
            <ScrollToTopButton querySelector={SCROLL_CONTAINER_SELECTOR} />
          </>
        ) : hasSearched && !getUserLoading ? (
          <>
            <Alert variant="warning" className="mb-4">
              <Alert.Heading as="h6" className="mb-2">
                <i className="fas fa-sad-tear mr-2" />
                No user found
              </Alert.Heading>
              {lastSearchTerm && (
                <div className="mb-1">
                  Searched <code>{lastSearchTerm}</code>.
                </div>
              )}
              {searchQueryError && (
                <div className="mb-1">
                  The server rejected the lookup: <code>{searchQueryError.message}</code>
                </div>
              )}
            </Alert>
            <PreviouslySearchedUsers
              handleInputChange={handleInputChange}
              handleSearchUrl={handleSearchUrl}
              loadExpanded={true}
              reRender={hasSearched}
            />
          </>
        ) : (
          <>
            <div className="mb-4">
              <span>
                <i className="fas fa-search m-2" />
                <i>Search for a user</i>
              </span>
            </div>
            <PreviouslySearchedUsers
              handleInputChange={handleInputChange}
              handleSearchUrl={handleSearchUrl}
              loadExpanded={true}
              reRender={hasSearched}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default UserView;
